/**
 * Data Models API Service
 * 
 * CRUD operations for data models + SSE streaming for semantic layer build.
 */

import { api, handleUnauthorizedResponse } from './client';
import { getApiUrl, STORAGE_KEYS } from './config';
import type { DataModelStatus } from './types';
import type { components } from './generated/api-types';
import { readSseStream, supportErrorMessage } from './sse';

export type ViewStatus = 'active' | 'invalid' | 'draft';
export type RelationshipStatus = 'proposed' | 'confirmed' | 'rejected';
export type DatasetPrimaryTimeColumnResponse = components['schemas']['DatasetPrimaryTimeColumnResponse'];
export interface JsonFieldPromotionKey { sourceColumn: string; path: string }
export interface JsonFieldPromotionOverrides {
  version: 1;
  include: JsonFieldPromotionKey[];
  exclude: JsonFieldPromotionKey[];
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ViewDescription {
  summary: string;
  what_it_shows: string;
  business_questions: string[] | string;
  why_important: string;
  typical_dashboard_usage: string;
}

export interface FilterHint {
  field: string;
  reason: string;
  label?: string;
  column_alias?: string;
  filter_type?: string;
  distinct_count?: number;
  priority?: number;
}

export interface DataModelView {
  id: string;
  name: string;
  title: string | null;
  sqlDefinition: string;
  description: string | null;
  descriptionJson: ViewDescription | null;
  viewType: string;
  sortOrder: number;
  factTable: string | null;
  grain: string | null;
  baseTables: string[];
  columnsJson: unknown[] | null;
  filterHintsJson: FilterHint[] | null;
  suggestedQuestions: string[] | null;
  keyMetrics: string[] | null;
  status: ViewStatus;
  validationError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DataModelRelationship {
  id: string;
  fromDatasetId: string;
  fromColumn: string;
  toDatasetId: string;
  toColumn: string;
  joinType: string;
  relationshipType: string;
  confidence: number;
  evidenceJson: Record<string, unknown> | null;
  status: RelationshipStatus;
  fromDataset: { id: string; name: string };
  toDataset: { id: string; name: string };
}

export interface DataModelDataset {
  id: string;
  datasetId: string;
  aliasName: string | null;
  dataset: {
    id: string;
    name: string;
    connectionId: string;
    parquetLocation: string;
    schemaJson: unknown;
    primaryTimeColumnOverride: string | null;
    jsonFieldPromotionOverrides: JsonFieldPromotionOverrides | null;
    profile: { profileJson: unknown } | null;
    connection: {
      id: string;
      name: string;
      connectorId: string;
    };
  };
}

// Re-export from types.ts (source of truth: swagger → api-types.ts)
export type { DataModelStatus } from './types';

export interface DataModelListItem {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  status: DataModelStatus;
  createdAt: string;
  updatedAt: string;
  datasetCount: number;
  viewCount: number;
  relationshipCount: number;
  connectionName: string | null;
  connectionId: string | null;
  connectorId: string | null;
  topViewNames: string[];
  previewTree: {
    factTable: string | null;
    relatedViews: string[];
    columns: string[];
  } | null;
}

export interface DataModelDetail {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  status: DataModelStatus;
  createdAt: string;
  updatedAt: string;
  datasets: DataModelDataset[];
  views: DataModelView[];
  relationships: DataModelRelationship[];
}

export interface CreateDataModelInput {
  name: string;
  connectionId: string;
  description?: string;
}

export interface UpdateDataModelInput {
  name?: string;
  description?: string;
  status?: DataModelStatus;
}

export interface BuildDataModelOptions {
  maxViews?: number;
  maxCandidates?: number;
  llmModel?: string;
  promptVersion?: string;
}

export interface BuildStreamHandlers {
  onJobStarted?: (data: { jobId: string }) => void;
  onProgress?: (stage: string, progress: number, message: string) => void;
  onCompleted?: (data: Record<string, unknown>) => void;
  onError?: (message: string, details?: unknown) => void;
  onDone?: () => void;
}

export interface BuildStatus {
  state: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  stage: string;
  message: string;
  error: string | null;
  steps: Array<{
    id: string;
    label: string;
    status: 'pending' | 'running' | 'done';
    message?: string;
  }>;
}

// ─── Types (views list) ─────────────────────────────────────────────────────

export interface DatasetViewColumnItem {
  name: string;
  type: string;
  semantic_type: string;
  default_aggregation: string | null;
  display_name?: string;
  source_entity?: string;
  entity_type?: string;
  is_calculated?: boolean;
  formula?: string;
  formula_description?: string;
  distinct_count?: number;
  source_kind?: 'json';
  source_dataset_id?: string;
  source_column?: string;
  source_path?: string;
}

export interface DatasetViewConnectionInfo {
  connectionId: string;
  connectionName: string;
  connectorId: string;
  connectorName: string;
}

export interface DatasetViewListItem {
  id: string;
  name: string;
  title: string | null;
  status: string;
  viewType: string;
  sortOrder: number;
  grain: string | null;
  suggestedQuestions: string[];
  keyMetrics: string[];
  dataModelId: string;
  dataModelName: string;
  measureCount: number;
  dimensionCount: number;
  updatedAt: string;
  connections: DatasetViewConnectionInfo[];
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

async function list(): Promise<DataModelListItem[]> {
  const response = await api.get<{ dataModels: DataModelListItem[] }>('/data-models');
  return response.dataModels;
}

async function listViews(): Promise<DatasetViewListItem[]> {
  const response = await api.get<{ views: DatasetViewListItem[] }>('/data-models/views');
  return response.views;
}

/**
 * Get columns for a specific dataset view (for field mapping editor).
 */
async function getViewColumns(viewId: string): Promise<DatasetViewColumnItem[]> {
  const response = await api.get<{ columns: DatasetViewColumnItem[] }>(`/data-models/views/${viewId}/columns`);
  return response.columns;
}

async function getById(id: string): Promise<DataModelDetail> {
  return api.get<DataModelDetail>(`/data-models/${id}`);
}

async function create(input: CreateDataModelInput): Promise<DataModelDetail> {
  return api.post<DataModelDetail>('/data-models', input);
}

async function update(id: string, input: UpdateDataModelInput): Promise<DataModelDetail> {
  return api.put<DataModelDetail>(`/data-models/${id}`, input);
}

async function updateDatasetPrimaryTimeColumn(
  dataModelId: string,
  datasetId: string,
  override: string | null,
): Promise<DatasetPrimaryTimeColumnResponse> {
  return api.put<DatasetPrimaryTimeColumnResponse>(
    `/data-models/${dataModelId}/datasets/${datasetId}/primary-time-column`,
    { override },
  );
}

async function updateDatasetJsonFieldPromotions(
  dataModelId: string,
  datasetId: string,
  overrides: JsonFieldPromotionOverrides,
): Promise<{ overrides: JsonFieldPromotionOverrides; jobId: string }> {
  return api.put(
    `/data-models/${dataModelId}/datasets/${datasetId}/json-field-promotions`,
    overrides,
  );
}

async function remove(id: string): Promise<void> {
  return api.delete(`/data-models/${id}`);
}

async function build(id: string, options?: BuildDataModelOptions): Promise<{ jobId: string }> {
  return api.post<{ jobId: string }>(`/data-models/${id}/build`, options || {});
}

async function getBuildStatus(jobId: string): Promise<BuildStatus> {
  return api.get<BuildStatus>(`/data-models/jobs/${jobId}/status`);
}

async function cancelBuild(jobId: string): Promise<{ jobId: string; status: string }> {
  return api.post<{ jobId: string; status: string }>(`/data-models/jobs/${jobId}/cancel`);
}

// ─── SSE streaming build ─────────────────────────────────────────────────────

async function streamBuild(
  dataModelId: string,
  options: BuildDataModelOptions,
  handlers: BuildStreamHandlers
): Promise<() => void> {
  const controller = new AbortController();
  const { signal } = controller;

  try {
    const url = getApiUrl(`/data-models/${dataModelId}/build-stream`);
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Correlation-ID': crypto.randomUUID(),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(options),
      signal,
    });

    if (!response.ok) {
      handleUnauthorizedResponse(response.status);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const processStream = async () => {
      try {
        await readSseStream(response.body!, ({ data }) => {
          try {
            const event = JSON.parse(data) as Record<string, unknown>;
            handleBuildEvent(event, handlers);
          } catch {
            // Skip malformed events
          }
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        throw err;
      }
    };

    processStream().catch((error) => {
      if (handlers.onError) {
        handlers.onError(error.message || 'Stream processing error');
      }
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return () => {};
    }
    if (handlers.onError) {
      handlers.onError(error instanceof Error ? error.message : 'Failed to start build stream');
    }
  }

  return () => {
    controller.abort();
  };
}

function handleBuildEvent(
  event: Record<string, unknown>,
  handlers: BuildStreamHandlers
): void {
  const type = event.type as string;

  if (type === 'job.started') {
    handlers.onJobStarted?.({ jobId: event.jobId as string });
    return;
  }

  if (type === 'done') {
    handlers.onDone?.();
    return;
  }

  if (type === 'error') {
    handlers.onError?.(supportErrorMessage(event), event);
    return;
  }

  // For full job events with data field
  const data = (event.data || event) as Record<string, unknown>;
  const stage = (data.stage || '') as string;
  const progress = (data.progress || 0) as number;
  const message = (data.message || '') as string;

  switch (type) {
    case 'job.progress':
      handlers.onProgress?.(stage, progress, message);
      break;
    case 'job.completed':
      handlers.onCompleted?.(data);
      break;
    case 'job.failed':
      handlers.onError?.(supportErrorMessage(data, 'Build failed'), data);
      break;
    case 'job.cancelled':
      handlers.onError?.('Build was cancelled');
      break;
    default:
      // Generic progress events from the runner
      if (stage && message) {
        handlers.onProgress?.(stage, progress, message);
      }
  }
}

// ─── Insights ────────────────────────────────────────────────────────────────

export interface InsightResult {
  id: string;
  insightDefinitionId: string;
  modelId: string;
  title: string;
  description: string;
  metricLabel: string | null;
  currentValue: number | null;
  previousValue: number | null;
  changePct: number | null;
  severity: 'high' | 'medium' | 'low';
  color: 'red' | 'green' | 'amber' | 'blue' | null;
  confidence: 'high' | 'medium' | 'low' | null;
  category: 'revenue' | 'pipeline' | 'engagement' | 'quality' | 'concentration' | 'conversion' | null;
  status: string;
  generatedAt: string;
  dataSnapshotAt: string | null;
  insightType: 'trend' | 'coverage' | 'concentration' | 'quality' | 'correlation';
  sourceViewId: string | null;
  sourceViewName: string | null;
}

export interface InsightResultsResponse {
  insights: InsightResult[];
  lastRefreshedAt: string | null;
}

async function getInsights(modelId: string): Promise<InsightResultsResponse> {
  return api.get<InsightResultsResponse>(`/data-models/${modelId}/insights`);
}

// ─── View Relationships ──────────────────────────────────────────────────────

export type ViewRelationshipItem = components['schemas']['ViewRelationshipItem'];

export interface ViewRelationshipsResponse {
  relationships: ViewRelationshipItem[];
}

async function getViewRelationships(viewId: string): Promise<ViewRelationshipItem[]> {
  const response = await api.get<ViewRelationshipsResponse>(`/data-models/views/${viewId}/relationships`);
  return response.relationships;
}

// ─── Export ──────────────────────────────────────────────────────────────────

export const dataModelsService = {
  list,
  listViews,
  getViewColumns,
  getViewRelationships,
  getById,
  create,
  update,
  updateDatasetPrimaryTimeColumn,
  updateDatasetJsonFieldPromotions,
  delete: remove,
  build,
  getBuildStatus,
  cancelBuild,
  streamBuild,
  getInsights,
};

export default dataModelsService;